import type { FC, RefObject, ChangeEvent } from 'react';
import React, { useState } from 'react';
import type { UrlTypeNameMapping } from '@pflegenavi/web-components';
import { DeleteDialog, Iconify } from '@pflegenavi/web-components';
import { Stack } from '@mui/material';
import type { FilePreviewType } from './UploadReceiptCard';
import { useTranslation } from 'react-i18next';
import { HiddenInputForReceiptFile } from './HiddenInputForReceiptFile';
import {
  ImageActionButton,
  ImagePreviewContainer,
} from './ImagePreviewContainer';
import { ImagesPreviewNavigationBar } from './imagesHandlingCard/ImagesPreviewNavigationBar';
import { EnlargeImageGallery } from '../EnlargeImageGallery';

interface ImagesHandlingFormProps {
  selectedImageIndex: number;
  receiptImageUrls: string[];
  imageUrlToTypeMap: UrlTypeNameMapping;
  file: FilePreviewType;
  inputRef: RefObject<HTMLInputElement | null>;
  onAddFile: (e: ChangeEvent) => void;
  onDeleteFile: (index: number) => void;
  onOpenSelectImageDialog: () => void;
  onSelectDifferentImage: (imageUrl: string) => void;
  canEdit: boolean;
  /** See `ImagePreviewContainer`; off where the preview column is narrow. */
  inlinePdfPreview?: boolean;
  /** Tooltip on the "add another" button; defaults to the receipt wording. */
  addFileLabel?: string;
}

export const ImagesHandlingForm: FC<ImagesHandlingFormProps> = ({
  receiptImageUrls,
  imageUrlToTypeMap,
  file,
  canEdit,
  onAddFile,
  onDeleteFile,
  inputRef,
  onOpenSelectImageDialog,
  selectedImageIndex,
  onSelectDifferentImage,
  inlinePdfPreview,
  addFileLabel,
}) => {
  const { t } = useTranslation();
  const [imageZoomed, setImageZoomed] = useState(false);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number>();

  const currentImageUrl = receiptImageUrls[selectedImageIndex];
  const inlinePreview = inlinePdfPreview !== false;

  return (
    <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
      <HiddenInputForReceiptFile inputRef={inputRef} onChangeFile={onAddFile} />

      <EnlargeImageGallery
        open={imageZoomed}
        handleClose={() => setImageZoomed(false)}
        initialSelectedIndex={selectedImageIndex}
        imageUrls={receiptImageUrls}
        imageUrlToTypeMap={imageUrlToTypeMap}
      />

      <DeleteDialog
        open={pendingDeleteIndex !== undefined}
        handleDelete={() => {
          if (pendingDeleteIndex !== undefined) {
            onDeleteFile(pendingDeleteIndex);
          }
          setPendingDeleteIndex(undefined);
        }}
        setDeleteDialogOpen={() => setPendingDeleteIndex(undefined)}
        confirmationMessage={t('confirmDeletionText')}
      />

      <Stack direction="row" alignItems="center" gap={1} sx={{ flexShrink: 0 }}>
        <ImagesPreviewNavigationBar
          imageUrls={receiptImageUrls}
          imageUrlToTypeMap={imageUrlToTypeMap}
          onSelectDifferentImage={onSelectDifferentImage}
          selectedImageUrl={currentImageUrl}
          onAddImage={canEdit ? onOpenSelectImageDialog : undefined}
          onDeleteImage={canEdit ? setPendingDeleteIndex : undefined}
          addImageLabel={addFileLabel}
        />
        {/* An inline PDF renders its own toolbar and swallows hover events, so
            the enlarge button cannot be overlaid on it (ENG-2295). It sits
            fixed at the end of the thumbnail row instead, with the scrollable
            thumbnails passing underneath it - for images too, so it does not
            move as the user switches between file types. */}
        {inlinePreview && (
          <ImageActionButton onClick={() => setImageZoomed(true)}>
            <Iconify icon="akar-icons:enlarge" fontSize={20} />
          </ImageActionButton>
        )}
      </Stack>

      <ImagePreviewContainer
        inlinePdfPreview={inlinePdfPreview}
        currentImageUrl={currentImageUrl}
        imageUrlToTypeMap={imageUrlToTypeMap}
        file={file}
        onEnlarge={() => setImageZoomed(true)}
      />
    </Stack>
  );
};
